import { API_URL } from "@/env";

// 5MB minimum for S3 multipart (except last part)
const BUFFER_SIZE = 5 * 1024 * 1024;

// WebM magic bytes for validation
const WEBM_MAGIC_BYTES = [0x1a, 0x45, 0xdf, 0xa3];

export interface UploadSession {
  sessionId: string;
  uploadId: string;
  partNumber: number;
  pendingChunks: Blob[];
  recordedChunks: Blob[];
  currentBufferSize: number;
  uploadQueue: Promise<void>;
  totalUploadedBytes: number;
  filename: string;
}

/**
 * Validates WebM header magic bytes
 */
const validateWebMHeader = (blob: Blob): Promise<boolean> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      if (reader.result instanceof ArrayBuffer) {
        const bytes = new Uint8Array(reader.result);
        const isValid = WEBM_MAGIC_BYTES.every((byte, i) => bytes[i] === byte);
        resolve(isValid);
      } else {
        resolve(false);
      }
    };
    reader.onerror = () => resolve(false);
    reader.readAsArrayBuffer(blob.slice(0, 4));
  });
};

/**
 * Converts a blob to base64 (without data URL prefix)
 * Uses ArrayBuffer approach for more reliable conversion of large blobs
 */
const blobToBase64 = (blob: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      if (reader.result instanceof ArrayBuffer) {
        // Convert ArrayBuffer to base64 using btoa
        const bytes = new Uint8Array(reader.result);

        // Convert bytes to binary string, then to base64
        let binary = '';
        const chunkSize = 0x8000; // 32KB chunks to avoid call stack issues
        for (let i = 0; i < bytes.length; i += chunkSize) {
          const chunk = bytes.subarray(i, Math.min(i + chunkSize, bytes.length));
          binary += String.fromCharCode.apply(null, Array.from(chunk));
        }

        try {
          const base64 = btoa(binary);
          resolve(base64);
        } catch (error) {
          console.error("❌ btoa() failed:", error);
          reject(error);
        }
      } else {
        console.error("❌ FileReader result is not ArrayBuffer:", typeof reader.result);
        reject(new Error("Failed to convert blob - result not ArrayBuffer"));
      }
    };

    reader.onerror = (error) => {
      console.error("❌ FileReader error:", error);
      reject(error);
    };

    reader.readAsArrayBuffer(blob);
  });
};

/**
 * Initialize the S3 multipart upload session
 */
export const initializeUpload = async (
  filename: string
): Promise<UploadSession> => {
  try {
    const response = await fetch(`${API_URL}/s3/progressive-upload/initiate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ filename }),
    });

    if (!response.ok) {
      throw new Error(`Failed to initiate upload: ${response.statusText}`);
    }

    const data = await response.json();

    return {
      sessionId: data.sessionId,
      uploadId: data.uploadId,
      partNumber: 0,
      pendingChunks: [],
      recordedChunks: [],
      currentBufferSize: 0,
      uploadQueue: Promise.resolve(),
      totalUploadedBytes: 0,
      filename,
    };
  } catch (error) {
    console.error("❌ Failed to initialize progressive upload:", error);
    throw error;
  }
};

/**
 * Flush buffered chunks to S3 as a single part
 */
const flushBuffer = async (
  session: UploadSession,
  isFinal: boolean = false
): Promise<void> => {
  if (session.pendingChunks.length === 0) {
    return;
  }

  const chunksToUpload = [...session.pendingChunks];
  const bufferSize = session.currentBufferSize;

  // Skip uploading if buffer is too small (less than 1KB) - unless it's the final flush
  if (bufferSize < 1024 && !isFinal) {
    console.warn(
      `⚠️ Skipping flush - buffer too small (${bufferSize} bytes)`
    );
    session.pendingChunks = [];
    session.currentBufferSize = 0;
    return;
  }

  // Clear buffer immediately so new chunks can be added
  session.pendingChunks = [];
  session.currentBufferSize = 0;
  session.partNumber++;

  const currentPart = session.partNumber;

  // Add to upload queue (non-blocking)
  session.uploadQueue = session.uploadQueue.then(async () => {
    try {
      // Combine buffered chunks into single blob
      const combinedBlob = new Blob(chunksToUpload, {
        type: chunksToUpload[0]?.type || "video/webm",
      });

      // Verify blob is readable and check first bytes
      try {
        // Check first 4 bytes to verify WebM header
        const headerBytes = await new Promise<Uint8Array>((resolve) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            if (reader.result instanceof ArrayBuffer) {
              resolve(new Uint8Array(reader.result));
            } else {
              resolve(new Uint8Array());
            }
          };
          reader.readAsArrayBuffer(combinedBlob.slice(0, 4));
        });

        const headerHex = Array.from(headerBytes).map(b => b.toString(16).padStart(2, '0')).join('');

        if (headerHex !== '1a45dfa3') {
          console.error(`   ❌ WARNING: Combined blob does not have valid WebM header!`);
          console.error(`   This may cause upload to fail on backend validation.`);
        }
      } catch (error) {
        console.error(`   ❌ Failed to verify blob:`, error);
      }

      // Convert to base64
      const base64Data = await blobToBase64(combinedBlob);

      // Verify base64 is valid
      if (base64Data.length < 100) {
        console.error(`   ❌ CRITICAL: Base64 data is suspiciously short!`);
        console.error(`   Base64 content:`, base64Data);
      }

      // Upload to backend
      const response = await fetch(`${API_URL}/s3/progressive-upload/chunk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: session.sessionId,
          chunkData: base64Data,
          partNumber: currentPart,
        }),
      });

      if (!response.ok) {
        throw new Error(
          `Failed to upload part ${currentPart}: ${response.statusText}`
        );
      }

      session.totalUploadedBytes += bufferSize;
    } catch (error) {
      console.error(`❌ Failed to upload part ${currentPart}:`, error);
      throw error;
    }
  });
};

/**
 * Add a chunk from MediaRecorder's ondataavailable event
 * Buffers chunks until 5MB is reached, then uploads
 */
export const addChunk = async (
  session: UploadSession,
  chunk: Blob
): Promise<void> => {
  if (!session.sessionId) {
    console.error("❌ Cannot add chunk: session not initialized");
    return;
  }

  if (chunk.size === 0) {
    console.warn("⚠️ Received empty chunk, skipping");
    return;
  }

  // Store chunk for local preview (always store, even tiny chunks)
  session.recordedChunks.push(chunk);

  // CRITICAL: Always include ALL chunks in upload buffer, even tiny ones
  // The WebM header can be split across multiple small chunks
  // Don't skip any chunks - they're all part of the video data

  // Validate WebM header on first LARGE chunk (skip tiny ones)
  if (session.partNumber === 0 && session.pendingChunks.length === 0) {
    // Only validate if chunk is large enough to contain header (at least 4 bytes)
    if (chunk.size >= 4) {
      const isValidWebM = await validateWebMHeader(chunk);
      if (!isValidWebM) {
        console.warn("⚠️ Chunk doesn't have WebM header - may be a continuation chunk");
        // Don't throw - just warn, as MediaRecorder might split the header across chunks
      }
    } else {
      console.warn("⚠️ First chunk too small to validate WebM header");
    }
  }

  // Add to buffer
  session.pendingChunks.push(chunk);
  session.currentBufferSize += chunk.size;

  // Flush buffer if we've reached 5MB (non-blocking)
  if (session.currentBufferSize >= BUFFER_SIZE) {
    flushBuffer(session); // Don't await - let it run in background
  }
};

/**
 * Get local blob for immediate preview (before S3 finalize completes)
 */
export const getLocalBlob = (session: UploadSession): Blob => {
  return new Blob(session.recordedChunks, {
    type: session.recordedChunks[0]?.type || "video/webm",
  });
};

/**
 * Finalize the upload after recording stops
 * Flushes any remaining buffered chunks and completes the S3 multipart upload
 */
export const finalizeUpload = async (
  session: UploadSession,
  reportId?: string
): Promise<{ url: string; key: string; size: number }> => {
  if (!session.sessionId) {
    throw new Error("Cannot finalize: session not initialized");
  }

  try {
    // Flush any remaining chunks in buffer (allow small buffers for final flush)
    if (session.pendingChunks.length > 0) {
      await flushBuffer(session, true); // isFinal = true
    }

    // Wait for all uploads to complete
    await session.uploadQueue;

    // Check if we have any parts uploaded
    if (session.partNumber === 0) {
      console.warn("⚠️ No parts were uploaded via progressive upload");

      // Abort the multipart upload since we didn't use it
      await abortUpload(session);

      // Fall back to uploading all chunks as a single blob
      const finalBlob = new Blob(session.recordedChunks, {
        type: session.recordedChunks[0]?.type || "video/webm",
      });

      if (finalBlob.size === 0) {
        throw new Error("No video data to upload - recording may have failed");
      }

      // Upload as a single chunk
      const base64Data = await blobToBase64(finalBlob);

      const response = await fetch(`${API_URL}/s3/progressive-upload/chunk`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: session.sessionId,
          chunkData: base64Data,
          partNumber: 1,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error("❌ Upload failed:", response.status, errorText);
        throw new Error(`Failed to upload blob: ${response.statusText} - ${errorText}`);
      }

      session.partNumber = 1; // Mark that we now have 1 part

      // Now finalize with multipart complete
      const finalizeResponse = await fetch(`${API_URL}/s3/progressive-upload/finalize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          sessionId: session.sessionId,
          useMultipartComplete: true,
          reportId,
        }),
      });

      if (!finalizeResponse.ok) {
        throw new Error(`Failed to finalize upload: ${finalizeResponse.statusText}`);
      }

      const result = await finalizeResponse.json();

      return result;
    }

    // Complete the S3 multipart upload
    const response = await fetch(`${API_URL}/s3/progressive-upload/finalize`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        sessionId: session.sessionId,
        useMultipartComplete: true,
        reportId,
      }),
    });

    if (!response.ok) {
      throw new Error(`Failed to finalize upload: ${response.statusText}`);
    }

    const result = await response.json();

    return result;
  } catch (error) {
    console.error("❌ Failed to finalize upload:", error);
    throw error;
  }
};

/**
 * Abort the upload (cleanup on cancel/error)
 */
export const abortUpload = async (session: UploadSession): Promise<void> => {
  if (!session.sessionId) {
    return;
  }

  try {
    const response = await fetch(`${API_URL}/s3/progressive-upload/abort`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ sessionId: session.sessionId }),
    });

    if (!response.ok) {
      console.warn("⚠️ Failed to abort upload on server");
    }
  } catch (error) {
    console.error("❌ Error aborting upload:", error);
  }
};

/**
 * Get upload statistics
 */
export const getUploadStats = (session: UploadSession) => {
  return {
    totalParts: session.partNumber,
    totalUploadedBytes: session.totalUploadedBytes,
    totalUploadedMB: (session.totalUploadedBytes / 1024 / 1024).toFixed(2),
    bufferedChunks: session.pendingChunks.length,
    bufferSize: session.currentBufferSize,
    bufferMB: (session.currentBufferSize / 1024 / 1024).toFixed(2),
  };
};
